import React, { useEffect, useMemo, useState } from 'react';
import { Button, Chip, Typography } from '@material-ui/core';
import { useForm, useFormState } from 'react-final-form';

type StorageMap = Record<string, any>;

type ManagerProps = {
    storagesById: StorageMap;
};

type BadgeProps = {
    storageId?: number | string | null;
    storagesById: StorageMap;
};

export const validateSMELines = (values: any, storagesById: StorageMap) => {
    if (!values?.isSME) return {};

    const errors: any = {};
    const lines = Array.isArray(values?.request_lines)
        ? values.request_lines
        : [];
    const lineErrors: any[] = [];
    let hasError = false;
    const seenStorageIds = new Set<number>();

    lines.forEach((line: any, index: number) => {
        const storageId = Number(line?.storageId);
        if (Number.isNaN(storageId) || storageId <= 0) return;

        if (seenStorageIds.has(storageId)) {
            lineErrors[index] = {
                ...(lineErrors[index] || {}),
                storageId: 'Duplicate storage is not allowed',
            };
            hasError = true;
        } else {
            seenStorageIds.add(storageId);
        }

        const storage = storagesById[String(storageId)];
        const isInventory = storage?.storagetype?.inventory;
        const qty = Number(line?.qty);

        if (isInventory === false) {
            if (line?.qty !== undefined && line?.qty !== null && qty !== 1) {
                lineErrors[index] = {
                    ...(lineErrors[index] || {}),
                    qty: 'Non-inventory quantity is fixed to 1',
                };
                hasError = true;
            }
            return;
        }

        const availableQty = Number(storage?.qty ?? storage?.storedQty ?? 0);
        if (
            !Number.isNaN(qty) &&
            !Number.isNaN(availableQty) &&
            qty > availableQty
        ) {
            lineErrors[index] = {
                ...(lineErrors[index] || {}),
                qty: `Qty cannot exceed available (${availableQty})`,
            };
            hasError = true;
        }
    });

    if (hasError) {
        errors.request_lines = lineErrors;
    }
    return errors;
};

export const SMEStorageTypeBadge: React.FC<BadgeProps> = ({
    storageId,
    storagesById,
}) => {
    const storage = storageId ? storagesById?.[String(storageId)] : null;
    if (!storage) return null;

    const isInventory = storage?.storagetype?.inventory;
    return (
        <div style={{ marginTop: 4, marginBottom: 8 }}>
            <Chip
                size="small"
                color={isInventory ? 'primary' : 'default'}
                label={isInventory ? 'Inventory' : 'Non-inventory'}
            />
        </div>
    );
};

export const SMELinesManager: React.FC<ManagerProps> = ({ storagesById }) => {
    const form = useForm();
    const { values } = useFormState({ subscription: { values: true } });
    const lines = Array.isArray(values?.request_lines)
        ? values.request_lines
        : [];
    const [dedupeMessage, setDedupeMessage] = useState('');

    const storageIdsSignature = lines
        .map((line: any) => line?.storageId || '')
        .join('|');

    useEffect(() => {
        const seen = new Map<number, number>();
        const duplicates: Array<{ storageId: number; index: number }> = [];

        lines.forEach((line: any, index: number) => {
            const storageId = Number(line?.storageId);
            if (Number.isNaN(storageId) || storageId <= 0) return;
            if (seen.has(storageId)) {
                duplicates.push({ storageId, index });
            } else {
                seen.set(storageId, index);
            }
        });

        if (!duplicates.length) return;

        duplicates.forEach(d => {
            form.change(`request_lines[${d.index}].storageId`, undefined);
            form.change(`request_lines[${d.index}].qty`, undefined);
        });

        const dedupedIds = Array.from(
            new Set(duplicates.map(d => d.storageId))
        );
        setDedupeMessage(
            `Removed duplicated storages automatically: ${dedupedIds.join(
                ', '
            )}`
        );
    }, [form, storageIdsSignature]);

    const qtyViolations = useMemo(() => {
        const violations: string[] = [];
        lines.forEach((line: any, index: number) => {
            const storageId = Number(line?.storageId);
            if (Number.isNaN(storageId) || storageId <= 0) return;

            const storage = storagesById[String(storageId)];
            const isInventory = storage?.storagetype?.inventory;
            if (isInventory === false) return;

            const availableQty = Number(
                storage?.qty ?? storage?.storedQty ?? 0
            );
            const qty = Number(line?.qty);
            if (
                !Number.isNaN(availableQty) &&
                !Number.isNaN(qty) &&
                qty > availableQty
            ) {
                violations.push(
                    `Line ${
                        index + 1
                    }: Qty ${qty} exceeds available ${availableQty}`
                );
            }
        });
        return violations;
    }, [lines, storagesById]);

    const applyMaxToAll = () => {
        const hasSelectedItems = lines.some((line: any) => {
            const storageId = Number(line?.storageId);
            return !Number.isNaN(storageId) && storageId > 0;
        });

        if (!hasSelectedItems) {
            setDedupeMessage('Please select items first');
            return;
        }

        setDedupeMessage('');
        lines.forEach((line: any, index: number) => {
            const storageId = Number(line?.storageId);
            if (Number.isNaN(storageId) || storageId <= 0) return;

            const storage = storagesById[String(storageId)];
            const isInventory = storage?.storagetype?.inventory;
            if (isInventory === false) {
                form.change(`request_lines[${index}].qty`, 1);
                return;
            }

            const availableQty = Number(
                storage?.qty ?? storage?.storedQty ?? 0
            );
            if (!Number.isNaN(availableQty)) {
                form.change(
                    `request_lines[${index}].qty`,
                    Math.max(availableQty, 0)
                );
            }
        });
    };

    return (
        <div style={{ margin: '8px 0 12px' }}>
            <Button
                size="small"
                color="primary"
                variant="outlined"
                onClick={applyMaxToAll}
            >
                Apply Max To All
            </Button>

            {!!dedupeMessage && (
                <Typography
                    variant="body2"
                    style={{ marginTop: 8, color: '#ef6c00' }}
                >
                    {dedupeMessage}
                </Typography>
            )}

            {qtyViolations.length > 0 && (
                <div style={{ marginTop: 8 }}>
                    <Typography variant="body2" style={{ color: '#d32f2f' }}>
                        Please fix quantity issues before saving:
                    </Typography>
                    {qtyViolations.map(v => (
                        <Typography
                            key={v}
                            variant="caption"
                            display="block"
                            style={{ color: '#d32f2f' }}
                        >
                            {v}
                        </Typography>
                    ))}
                </div>
            )}
        </div>
    );
};
